/**
 * 预约填表项（type=39）数据模型工具
 * 与微信小程序端 utils/appointmentField.js 对齐（同一个后端，数据结构必须一致）
 * 零依赖纯函数，保证可用 node 直接验证
 */

export const APPOINTMENT_FIELD_TYPE = 39;

export const TIME_SLOT_TYPE = {
  CUSTOM: 1, // 单独设置时段（每个日期独立时段）
  CYCLE: 2, // 统一设置时段（一组时段应用到所有日期）
};

let uidCounter = 0;
function genRandomDecimal() {
  uidCounter += 1;
  return Date.now() * 1000 + (uidCounter % 1000);
}

/**
 * 创建预约选项
 * @param {string} optionName 选项名
 */
export function createAppointmentOption(optionName = "") {
  return {
    optionName,
    sortOrder: 0,
    timeSlotType: TIME_SLOT_TYPE.CUSTOM,
    timeSlots: [],
    maxNum: -1,
    randomDecimal: genRandomDecimal(),
  };
}

/**
 * 名额归一化：空/null/undefined/NaN → -1（不限），0 合法，否则取整
 */
export function normalizeQuota(value) {
  if (value === "" || value === null || value === undefined) return -1;
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return -1;
  return n;
}

/**
 * 解析 datetime 字符串为时间戳；replace 兼容 iOS；非法返回 NaN
 */
export function parseDateTime(str) {
  if (!str) return NaN;
  return new Date(String(str).replace(/-/g, "/")).getTime();
}

/**
 * 日期补零：2026-7-8 → 2026-07-08（防御后端旧数据）
 */
export function padDateStr(dateStr) {
  if (!dateStr) return "";
  const parts = String(dateStr).split("-");
  if (parts.length !== 3) return String(dateStr);
  return `${parts[0]}-${String(parts[1]).padStart(2, "0")}-${String(
    parts[2]
  ).padStart(2, "0")}`;
}

/**
 * 提取 HH:mm：2026-07-10 09:00:00 → 09:00
 */
export function extractTime(dateTimeStr) {
  if (!dateTimeStr) return "";
  const timePart = String(dateTimeStr).split(" ")[1] || "";
  return timePart.substring(0, 5);
}

/**
 * 拼接完整 datetime：date + HH:mm → yyyy-MM-dd HH:mm:00
 */
export function buildSlotDateTime(dateStr, hhmm) {
  return `${padDateStr(dateStr)} ${hhmm}:00`;
}

/**
 * 时刻级重叠检查（HH:mm 形态）；首尾相接不算重叠；非法项丢弃
 */
export function hasOverlappingPeriods(timeList) {
  const toMinutes = (t) => {
    const parts = String(t).split(":");
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
    return h * 60 + m;
  };
  const items = (timeList || [])
    .map((it) => ({ start: toMinutes(it.startTime), end: toMinutes(it.endTime) }))
    .filter((it) => !Number.isNaN(it.start) && !Number.isNaN(it.end))
    .sort((a, b) => a.start - b.start);
  let maxEnd = -Infinity;
  for (const it of items) {
    if (it.start < maxEnd) return true;
    if (it.end > maxEnd) maxEnd = it.end;
  }
  return false;
}

/**
 * 完整 datetime 形态重叠检查（可跨日期）；首尾相接不算重叠
 */
export function hasOverlappingSlots(timeSlots) {
  const items = (timeSlots || [])
    .filter((s) => s && s.startTime && s.endTime)
    .map((s) => ({ start: parseDateTime(s.startTime), end: parseDateTime(s.endTime) }))
    .filter((it) => !Number.isNaN(it.start) && !Number.isNaN(it.end))
    .sort((a, b) => a.start - b.start);
  let maxEnd = -Infinity;
  for (const it of items) {
    if (it.start < maxEnd) return true;
    if (it.end > maxEnd) maxEnd = it.end;
  }
  return false;
}

/**
 * 预约填表项保存前校验（与小程序 validateAppointmentField 对齐，按序短路）
 * 注意：选项可以没有 timeSlots（开放式预约），校验放行
 */
export function validateAppointmentField(field) {
  if (!field || field.type !== APPOINTMENT_FIELD_TYPE) {
    return { valid: false, message: "不是预约填表项" };
  }
  const list = Array.isArray(field.appointmentList) ? field.appointmentList : [];
  if (field.isRequired === 1 && list.length === 0) {
    return { valid: false, message: "至少添加一个预约选项" };
  }
  if (list.length === 0) {
    return { valid: true, message: "" };
  }
  for (const option of list) {
    if (!option.optionName || !String(option.optionName).trim()) {
      return { valid: false, message: "预约选项名称不能为空" };
    }
    const slots = Array.isArray(option.timeSlots) ? option.timeSlots : [];
    for (const slot of slots) {
      if (!slot.startTime || !slot.endTime) {
        return { valid: false, message: "时间段开始和结束时间不能为空" };
      }
      if (parseDateTime(slot.endTime) <= parseDateTime(slot.startTime)) {
        return { valid: false, message: "结束时间必须晚于开始时间" };
      }
      if (normalizeQuota(slot.quota) < -1) {
        return { valid: false, message: "名额必须 >= -1" };
      }
    }
    if (hasOverlappingSlots(slots)) {
      return { valid: false, message: "时间段不能重叠" };
    }
  }
  return { valid: true, message: "" };
}

/**
 * 扁平 timeSlots → 按日期分组；日期升序、组内 startTime 升序
 */
export function groupTimeSlotsByDate(timeSlots) {
  const map = {};
  (timeSlots || []).forEach((slot) => {
    const date =
      slot && slot.startTime ? String(slot.startTime).split(" ")[0] : "";
    if (!date) return;
    if (!map[date]) map[date] = [];
    map[date].push(slot);
  });
  return Object.keys(map)
    .sort()
    .map((date) => ({
      date,
      slots: map[date]
        .slice()
        .sort((a, b) => parseDateTime(a.startTime) - parseDateTime(b.startTime)),
    }));
}

/**
 * 选项时间摘要：已设置N个可预约日期（N=timeSlots 条数，与小程序现状一致）
 */
export function getAppointmentOptionSummary(option) {
  const count =
    option && Array.isArray(option.timeSlots) ? option.timeSlots.length : 0;
  return count > 0 ? `已设置${count}个可预约日期` : "";
}

/**
 * 复制活动时清零：所有 slot 的 selectCount=0、remaining=normalizeQuota(quota)
 * 原地修改，避免「已有报名」误判锁死配置
 */
export function resetAppointmentTimeSlotCounts(field) {
  if (!field || !Array.isArray(field.appointmentList)) return;
  field.appointmentList.forEach((option) => {
    (option.timeSlots || []).forEach((slot) => {
      slot.selectCount = 0;
      slot.remaining = normalizeQuota(slot.quota);
    });
  });
}

/**
 * cycle（统一时段）模式保存：selectedDates × 时段模板 笛卡尔展开为完整 datetime slots
 */
export function expandCycleSlots(selectedDates, cycleSlots) {
  const result = [];
  (selectedDates || []).forEach((date) => {
    (cycleSlots || []).forEach((tpl) => {
      result.push({
        startTime: buildSlotDateTime(date, tpl.startTime),
        endTime: buildSlotDateTime(date, tpl.endTime),
        quota: normalizeQuota(tpl.quota),
      });
    });
  });
  return result.sort(
    (a, b) => parseDateTime(a.startTime) - parseDateTime(b.startTime)
  );
}
